/**
 * resolveCierreDemoData.ts — Ventas/cierre canónicos del demo stress-test (id fijo).
 *
 * Unifica preview del ticket, reporte detallado e histórico para el cierre demo
 * aunque localStorage tenga snapshot legacy con menos ventas.
 */

import type { CierreCaja, Venta } from "../types/caja.types";
import { sumVentasPorMetodo, totalMontos } from "../utils/propinaAllocation";
import {
  buildMay12CierreCaja,
  MAY12_VENTAS_MAIN,
} from "./may12CajaDemoSeed.data";
import { getMulticajaDemoCierreIds } from "./multicajaJornadaDemo.data";
import { ANEXO_B_CIERRE_DEMO_ID } from "./anexoBCierreDemo.data";

export const DEMO_CIERRE_REF_ID = "1778595754641";

export const DEMO_CIERRE_REF_IDS = [
  ...getMulticajaDemoCierreIds(),
  ANEXO_B_CIERRE_DEMO_ID,
];

export function isDemoCierreRef(cierre: Pick<CierreCaja, "id">): boolean {
  return cierre.id === DEMO_CIERRE_REF_ID;
}

/** Ventas activas del seed canónico (13 POS, 10 empleados). */
export function getDemoCierreVentasCanonicas(): Venta[] {
  return MAY12_VENTAS_MAIN.filter((v) => v.estado !== "anulada");
}

export function resolveVentasForCierreDemo(
  cierre: CierreCaja,
  ventas: Venta[],
): Venta[] {
  if (
    cierre.id === ANEXO_B_CIERRE_DEMO_ID &&
    (cierre.ventasSnapshot?.length ?? 0) > 0
  ) {
    return cierre.ventasSnapshot ?? ventas;
  }
  if (!isDemoCierreRef(cierre)) return ventas;
  const activas = ventas.filter((v) => v.estado !== "anulada");
  if (activas.length >= 6) return ventas;
  const canonical = getDemoCierreVentasCanonicas();
  if (canonical.length === 0) return ventas;
  return canonical.map((v) => ({
    ...v,
    sesionId: cierre.sesionId ?? v.sesionId,
  }));
}

/** Inyecta auditoría ciega + métricas del stress-test solo si el snapshot es legacy. */
export function resolveCierreForTicketDemo(cierre: CierreCaja): CierreCaja {
  if (!isDemoCierreRef(cierre)) return cierre;
  const snapActivas =
    cierre.ventasSnapshot?.filter((v) => v.estado !== "anulada").length ?? 0;
  const ref = buildMay12CierreCaja();
  if (snapActivas >= 6) {
    return {
      ...cierre,
      cierreCiegoAudit: cierre.cierreCiegoAudit ?? ref.cierreCiegoAudit,
      conteoEfectivo: cierre.conteoEfectivo ?? ref.conteoEfectivo,
    };
  }
  const ventas = getDemoCierreVentasCanonicas();
  return {
    ...cierre,
    montoEsperado: ref.montoEsperado,
    diferencia: ref.diferencia,
    ventasCount: ventas.length,
    ventasSnapshot: ref.ventasSnapshot,
    totalVentasGeneral: ref.totalVentasGeneral,
    totalVentasEfectivo: ref.totalVentasEfectivo,
    cierreCiegoAudit: cierre.cierreCiegoAudit ?? ref.cierreCiegoAudit,
    conteoEfectivo: cierre.conteoEfectivo ?? ref.conteoEfectivo,
  };
}

/** Métricas de lista/historial desde el snapshot del cierre (sin inyectar stress-test). */
export function resolveCierreMetricasHistorial(cierre: CierreCaja): {
  diferencia: number;
  montoEsperado: number;
  totalVentasGeneral: number;
  ventasCount: number | undefined;
} {
  const ventasSnap =
    cierre.ventasSnapshot?.filter((v) => v.estado !== "anulada") ?? [];
  const totalCobrado =
    ventasSnap.length > 0
      ? totalMontos(sumVentasPorMetodo(ventasSnap))
      : (cierre.totalVentasGeneral ?? 0);

  return {
    diferencia: cierre.diferencia ?? 0,
    montoEsperado:
      cierre.montoEsperado ??
      cierre.montoInicial +
        (cierre.totalVentasEfectivo ?? 0) +
        (cierre.totalIngresosEfectivo ?? 0) -
        (cierre.totalRetiros ?? 0),
    totalVentasGeneral: totalCobrado,
    ventasCount: ventasSnap.length > 0 ? ventasSnap.length : cierre.ventasCount,
  };
}
